import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type JSX } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { formatKpiUnit } from '@/components/kpis/KpiUnitDisplay';
import { LogsheetEntryForm } from '@/components/logsheets-maker/LogsheetEntryForm';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useMakerActiveWorkOrdersQuery,
  useMakerKpisForWorkOrderMachineQuery,
} from '@/hooks/useLogsheetMakerQueries';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { uploadStagedFiles } from '@/lib/logsheets-maker/uploadStagedFiles';
import { toast } from '@/lib/toast';
import { formatMachineName } from '@/lib/utils/machine-label';
import {
  createLogsheetMakerRequest,
  submitLogsheetMakerRequest,
} from '@/services/logsheets-maker';
import { listMachinesRequest } from '@/services/machines';
import type {
  CreateLogsheetMakerBody,
  MakerKpiForMachine,
  MakerWorkOrderListItem,
  UpdateLogsheetMakerBody,
} from '@/types/logsheets-maker';
import type { MachineListItem } from '@/types/machines';

/**
 * D-229 — CreateMakerLogsheetWizardPage (refactored)
 *
 * Two-step wizard:
 *   Step 1 — Selection: Work Order (required) → Site (from WO's active sites) →
 *             Machine (from WO's linked machines filtered by chosen site) →
 *             Entry Type (Work | Idle).
 *             "Next" is disabled until all four are picked.
 *   Step 2 — Form: KPI inputs (from useMakerKpisForWorkOrderMachineQuery) +
 *             LogsheetEntryForm for date/shift/times. WO path always active.
 *
 * No-WO path and legacy MachineType KPI rendering branch have been removed (D-229).
 */

interface Selection {
  readonly siteId: number;
  readonly siteName: string;
  readonly machineId: number;
  readonly machineName: string;
  readonly machineCode: string;
  readonly vehicleNumber: string | null;
  readonly machineTypeId: number;
  readonly machineTypeName: string;
  readonly type: import('@/types/logsheets-maker').EntryType;
}

export function CreateMakerLogsheetWizardPage(): JSX.Element {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<1 | 2>(1);
  const [workOrderId, setWorkOrderId] = useState<number | undefined>(undefined);
  const [siteId, setSiteId] = useState<number | undefined>(undefined);
  const [machineId, setMachineId] = useState<number | undefined>(undefined);
  const [entryType, setEntryType] = useState<import('@/types/logsheets-maker').EntryType | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // KPI values collected from kpi:{id} inputs when WO path is active.
  const [kpiValues, setKpiValues] = useState<Record<string, string>>({});
  // Validation error message for KPI inputs.
  const [kpiValuesError, setKpiValuesError] = useState<string | null>(null);
  // Machines for the selected WO filtered to the chosen site.
  const [woSiteMachines, setWoSiteMachines] = useState<readonly MachineListItem[]>([]);
  // Inline-attach: files staged in memory; upload after the create POST returns.
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  // True while the create / upload / submit chain is in flight.
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Clear downstream selections when WO changes.
  useEffect(() => {
    setSiteId(undefined);
    setMachineId(undefined);
    setKpiValues({});
    setWoSiteMachines([]);
  }, [workOrderId]);

  // Clear machine when site changes.
  useEffect(() => {
    setMachineId(undefined);
  }, [siteId]);

  // Fetch all active WOs (no siteId filter — WO is selected first).
  const activeWosQuery = useMakerActiveWorkOrdersQuery();

  const selectedWo: MakerWorkOrderListItem | undefined =
    workOrderId !== undefined
      ? (activeWosQuery.data ?? []).find((wo) => wo.id === workOrderId)
      : undefined;

  // Active sites from the selected WO.
  const woSites = selectedWo?.sites ?? [];

  // When site changes, compute available machines: WO's machines whose siteId matches.
  // We fetch the machine details to get machineTypeId/machineTypeName via listMachinesRequest.
  useEffect(() => {
    if (selectedWo === undefined || siteId === undefined) {
      setWoSiteMachines([]);
      return;
    }
    const woMachineIds = new Set(selectedWo.machines.map((m) => m.machineId));
    // Fetch all active machines for the site, then filter to only those in the WO.
    void listMachinesRequest({ siteId, status: 'active', pageSize: 200 })
      .then((result) => {
        setWoSiteMachines(result.items.filter((m) => woMachineIds.has(m.id)));
      })
      .catch(() => {
        setWoSiteMachines([]);
      });
  }, [selectedWo, siteId]);

  // KPIs for the selected (WO, machine) pair.
  const makerKpisQuery = useMakerKpisForWorkOrderMachineQuery(
    workOrderId ?? null,
    machineId ?? null,
  );
  const makerKpis: readonly MakerKpiForMachine[] = makerKpisQuery.data ?? [];

  const selectedMachine = woSiteMachines.find((m) => m.id === machineId);
  const selectedSite = woSites.find((s) => s.siteId === siteId);

  const allPickedStep1 =
    workOrderId !== undefined &&
    siteId !== undefined &&
    machineId !== undefined &&
    entryType !== undefined;

  if (!permissions.includes('logsheet.maker.create')) {
    return <Navigate to="/dashboard" replace />;
  }

  function handleNext(): void {
    if (!allPickedStep1) return;
    setStep(2);
  }

  function buildSelection(): Selection | null {
    if (
      siteId === undefined ||
      selectedSite === undefined ||
      machineId === undefined ||
      selectedMachine === undefined ||
      entryType === undefined
    ) {
      return null;
    }
    return {
      siteId,
      siteName: selectedSite.siteName,
      machineId,
      machineName: selectedMachine.name,
      machineCode: selectedMachine.code,
      vehicleNumber: selectedMachine.vehicleNumber,
      machineTypeId: selectedMachine.machineTypeId,
      machineTypeName: selectedMachine.machineTypeName,
      type: entryType,
    };
  }

  function validateWoKpiValues(): string | null {
    for (const kpi of makerKpis) {
      const raw = kpiValues[`kpi:${String(kpi.id)}`];
      if (raw === undefined || raw.trim() === '') {
        return `${kpi.name}: required numeric value`;
      }
      const num = Number(raw);
      if (!Number.isFinite(num)) {
        return `${kpi.name}: must be a valid number`;
      }
    }
    return null;
  }

  function buildCreateBody(
    base: CreateLogsheetMakerBody | UpdateLogsheetMakerBody,
    status: 'Draft' | 'Submitted',
  ): CreateLogsheetMakerBody | null {
    if (!('siteId' in base)) return null;
    if (workOrderId === undefined) return null;
    // F-8: KPI values only apply to Work entries. Idle entries record downtime and carry no KPI
    // production — the API accepts an empty kpiValues map for Idle, so skip both the required-value
    // gate and the value collection (previously the wizard demanded a value for every KPI regardless
    // of entry type, making it impossible to save an Idle entry).
    const isWork = entryType === 'Work';
    if (isWork && makerKpis.length > 0) {
      const kpiErr = validateWoKpiValues();
      if (kpiErr !== null) {
        setKpiValuesError(kpiErr);
        return null;
      }
    }
    setKpiValuesError(null);
    const mergedKpiValues = isWork
      ? Object.fromEntries(Object.entries(kpiValues).map(([k, v]) => [k, Number(v)]))
      : {};
    return {
      ...base,
      kpiValues: mergedKpiValues,
      status,
      workOrderId,
    };
  }

  /**
   * Orchestrate create → upload (if any) → optional submit. Used by both
   * Save Draft and Submit handlers. When the user clicked Submit AND has
   * staged files, the create POST is forced to Draft so the document
   * upload endpoint accepts the writes (it 409s on Submitted entries),
   * then the submit endpoint is called after uploads finish.
   */
  async function performSave(
    body: CreateLogsheetMakerBody | UpdateLogsheetMakerBody,
    intent: 'Draft' | 'Submitted',
  ): Promise<void> {
    setErrorMessage(null);
    const hasStagedFiles = stagedFiles.length > 0;
    // Force Draft for the create POST when we'll need to attach docs first.
    const initialStatus: 'Draft' | 'Submitted' =
      intent === 'Submitted' && hasStagedFiles ? 'Draft' : intent;
    const createBody = buildCreateBody(body, initialStatus);
    if (createBody === null) return;

    setSubmitting(true);
    try {
      // ── 1. Create ────────────────────────────────────────────────────
      let created;
      try {
        created = await createLogsheetMakerRequest(createBody);
      } catch (e) {
        const err = e instanceof ApiError ? e : null;
        const detail = err?.detail ?? err?.message ?? 'Could not save logsheet.';
        setErrorMessage(detail);
        toast.error('Could not save logsheet', { description: detail });
        return;
      }

      // ── 2. Upload staged files (if any) ──────────────────────────────
      const uploadOutcome = hasStagedFiles
        ? await uploadStagedFiles(created.id, stagedFiles)
        : { ok: [], failed: [] };

      // ── 3. Optional submit (only when we deferred the submit) ────────
      const deferredSubmit = intent === 'Submitted' && hasStagedFiles;
      let finalStatus: 'Draft' | 'Submitted' = created.status as 'Draft' | 'Submitted';
      let submitFailed = false;

      if (deferredSubmit) {
        // If literally every staged file failed, leave as Draft so the
        // user can retry uploads from the edit page before submitting.
        const allUploadsFailed =
          uploadOutcome.failed.length === stagedFiles.length;
        if (!allUploadsFailed) {
          try {
            const submitted = await submitLogsheetMakerRequest(created.id);
            finalStatus = submitted.status as 'Draft' | 'Submitted';
          } catch (e) {
            submitFailed = true;
            const err = e instanceof ApiError ? e : null;
            toast.error('Documents attached, but submission failed', {
              description: err?.detail ?? err?.message ?? 'Retry from the edit page.',
            });
          }
        }
      }

      // ── 4. Toast summary ─────────────────────────────────────────────
      const failedNames = uploadOutcome.failed.map((f) => f.file.name).join(', ');
      const successCount = uploadOutcome.ok.length;
      const totalFiles = stagedFiles.length;

      if (submitFailed) {
        // Already toasted above.
      } else if (deferredSubmit && uploadOutcome.failed.length === stagedFiles.length) {
        toast.warning('Saved as Draft, but no documents attached', {
          description: `Failed: ${failedNames}. Retry uploads from the edit page; submit when ready.`,
        });
      } else if (uploadOutcome.failed.length > 0) {
        const verb = finalStatus === 'Submitted' ? 'Submitted' : 'Saved';
        toast.warning(
          `${verb}. ${String(successCount)} of ${String(totalFiles)} documents attached`,
          { description: `Failed: ${failedNames}. Retry from the entry's edit page.` },
        );
      } else if (hasStagedFiles) {
        const verb = finalStatus === 'Submitted' ? 'Submitted' : 'Draft saved';
        toast.success(
          `${verb}. ${String(totalFiles)} document${totalFiles === 1 ? '' : 's'} attached.`,
        );
      } else {
        const verb = finalStatus === 'Submitted' ? 'submitted' : 'saved';
        toast.success(`Logsheet ${verb}`);
      }

      void queryClient.invalidateQueries({ queryKey: ['logsheets-maker'] });
      void navigate(`/logsheets/view/${String(created.id)}`);
    } finally {
      setSubmitting(false);
    }
  }

  function onSaveDraft(body: CreateLogsheetMakerBody | UpdateLogsheetMakerBody): void {
    void performSave(body, 'Draft');
  }

  function onSubmit(body: CreateLogsheetMakerBody | UpdateLogsheetMakerBody): void {
    void performSave(body, 'Submitted');
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">New logsheet entry</h1>
        <p className="text-muted-foreground">
          Step {step} of 2 —{' '}
          {step === 1 ? 'pick work order, site, machine, and entry type' : 'fill the form'}.
        </p>
      </div>

      {step === 1 && (
        <Card data-testid="wizard-step-1">
          <CardHeader>
            <CardTitle>Selection</CardTitle>
            <CardDescription>
              Choose the work order, site, machine, and entry type.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Work Order — required first selection */}
            <div className="space-y-1">
              <Label htmlFor="wizard-wo">Work Order</Label>
              <Select
                value={workOrderId !== undefined ? String(workOrderId) : ''}
                onValueChange={(v) => {
                  const n = Number(v);
                  if (Number.isFinite(n) && n > 0) setWorkOrderId(n);
                }}
                disabled={activeWosQuery.isLoading}
              >
                <SelectTrigger id="wizard-wo" data-testid="wizard-wo-trigger">
                  <SelectValue placeholder="Select work order…" />
                </SelectTrigger>
                <SelectContent>
                  {(activeWosQuery.data ?? []).map((wo) => (
                    <SelectItem
                      key={wo.id}
                      value={String(wo.id)}
                      data-testid={`wizard-wo-option-${String(wo.id)}`}
                    >
                      {wo.woNumber} —{' '}
                      {/* eslint-disable-next-line @typescript-eslint/no-deprecated -- legacy `client` field fallback per Plan 22-03 D-227 */}
                      {wo.clientName ?? wo.client ?? ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Site — scoped to WO's active sites */}
            <div className="space-y-1">
              <Label htmlFor="wizard-site">Site</Label>
              <Select
                value={siteId !== undefined ? String(siteId) : ''}
                onValueChange={(v) => {
                  const n = Number(v);
                  if (Number.isFinite(n) && n > 0) setSiteId(n);
                }}
                disabled={workOrderId === undefined || woSites.length === 0}
              >
                <SelectTrigger id="wizard-site" data-testid="wizard-site-trigger">
                  <SelectValue
                    placeholder={workOrderId === undefined ? 'Pick work order first…' : 'Select site…'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {woSites.map((s) => (
                    <SelectItem
                      key={s.siteId}
                      value={String(s.siteId)}
                      data-testid={`wizard-site-option-${String(s.siteId)}`}
                    >
                      {s.siteName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Machine — scoped to WO machines at selected site */}
            <div className="space-y-1">
              <Label htmlFor="wizard-machine">Machine</Label>
              <Select
                value={machineId !== undefined ? String(machineId) : ''}
                onValueChange={(v) => {
                  const n = Number(v);
                  if (Number.isFinite(n) && n > 0) setMachineId(n);
                }}
                disabled={siteId === undefined || woSiteMachines.length === 0}
              >
                <SelectTrigger id="wizard-machine" data-testid="wizard-machine-trigger">
                  <SelectValue
                    placeholder={siteId === undefined ? 'Pick site first…' : 'Select machine…'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {woSiteMachines.map((m) => (
                    <SelectItem
                      key={m.id}
                      value={String(m.id)}
                      data-testid={`wizard-machine-option-${String(m.id)}`}
                    >
                      {formatMachineName(m.name, m.vehicleNumber, m.code)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Entry type */}
            <div className="space-y-1">
              <Label>Entry type</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={entryType === 'Work' ? 'default' : 'outline'}
                  onClick={() => { setEntryType('Work'); }}
                  data-testid="wizard-type-work"
                >
                  Work
                </Button>
                <Button
                  type="button"
                  variant={entryType === 'Idle' ? 'default' : 'outline'}
                  onClick={() => { setEntryType('Idle'); }}
                  data-testid="wizard-type-idle"
                >
                  Idle
                </Button>
              </div>
            </div>

            <div className="pt-2">
              <Button
                type="button"
                onClick={handleNext}
                disabled={!allPickedStep1}
                data-testid="wizard-next"
              >
                Next
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card data-testid="wizard-step-2">
          <CardHeader>
            <CardTitle>Entry details</CardTitle>
            <CardDescription>
              Fill the entry. Save Draft to keep working; Submit when ready for the checker.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(() => {
              const sel = buildSelection();
              if (sel === null) return null;
              // WO-scoped KPI block — passed into LogsheetEntryForm so it
              // renders inside Section 3 (Details), not above the context.
              // F-8: Idle entries carry no KPI production, so render an explanatory note instead of
              // the (required) KPI inputs — keeping the form submittable for downtime entries.
              const kpiSlot = sel.type !== 'Work' ? (
                <p className="text-sm text-muted-foreground" data-testid="wizard-kpi-idle-note">
                  KPI values are not required for Idle entries.
                </p>
              ) : (
                <div className="space-y-3" data-testid="wizard-kpi-wo-section">
                  <Label className="text-sm font-medium">KPI values</Label>
                  {makerKpisQuery.isLoading && (
                    <p className="text-sm text-muted-foreground" data-testid="wizard-kpi-loading">
                      Loading KPIs…
                    </p>
                  )}
                  {makerKpisQuery.isError && (
                    <p className="text-sm text-destructive" data-testid="wizard-kpi-error">
                      Failed to load KPIs for this work order.
                    </p>
                  )}
                  {!makerKpisQuery.isLoading && makerKpis.length === 0 && (
                    <p className="text-sm text-muted-foreground" data-testid="wizard-kpi-empty">
                      No KPIs defined for this Work Order.
                    </p>
                  )}
                  {makerKpis.length > 0 && (
                    <div className="space-y-3">
                      {makerKpis.map((kpi) => (
                        <div key={kpi.id} className="space-y-1">
                          <Label htmlFor={`wizard-kpi-${String(kpi.id)}`}>
                            {kpi.name}{' '}
                            <span className="text-xs text-muted-foreground">
                              ({formatKpiUnit(kpi.unit, kpi.customUnitText)})
                            </span>
                            {' '}<span className="text-xs text-destructive">*</span>
                          </Label>
                          <Input
                            id={`wizard-kpi-${String(kpi.id)}`}
                            data-testid={`wizard-kpi-input-${String(kpi.id)}`}
                            type="number"
                            value={kpiValues[`kpi:${String(kpi.id)}`] ?? ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              setKpiValuesError(null);
                              setKpiValues((prev) => ({
                                ...prev,
                                [`kpi:${String(kpi.id)}`]: val,
                              }));
                            }}
                          />
                        </div>
                      ))}
                      {kpiValuesError !== null && (
                        <p
                          className="text-sm text-destructive"
                          data-testid="wizard-kpi-validation-error"
                        >
                          {kpiValuesError}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
              return (
                <LogsheetEntryForm
                  mode="create"
                  context={{
                    siteId: sel.siteId,
                    siteName: sel.siteName,
                    machineId: sel.machineId,
                    machineName: sel.machineName,
                    machineCode: sel.machineCode,
                    vehicleNumber: sel.vehicleNumber,
                    machineTypeId: sel.machineTypeId,
                    machineTypeName: sel.machineTypeName,
                    type: sel.type,
                    schema: [],
                  }}
                  submitting={submitting}
                  errorMessage={errorMessage}
                  onSaveDraft={onSaveDraft}
                  onSubmit={onSubmit}
                  hideKpiSection
                  kpiSlot={kpiSlot}
                  stagedFiles={stagedFiles}
                  onStagedFilesChange={setStagedFiles}
                />
              );
            })()}
            <div className="mt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => { setStep(1); }}
                data-testid="wizard-back"
                disabled={submitting}
              >
                Back
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
